/**
 * Dynamic Column Definitions for Product Gate
 *
 * Provides column configurations that adapt based on the Hyperlane feature flag.
 * When Hyperlane is enabled, columns use Enterprise naming conventions.
 * When disabled (Legacy mode), columns use RADAR naming conventions.
 *
 * Column Mapping (Hyperlane ON):
 * - Title → Title Name
 * - Product ID → Enterprise Product ID
 * - Product Number → Enterprise Product Number
 * - Group ID → Enterprise Group ID
 * - Episode Number → Enterprise Episode Number
 * - (new) → Parent Season Name
 * - (new) → Releasing Company (ESPN+ only)
 *
 * Usage:
 * ```
 * import { useProductGateColumns } from './dynamicColumns';
 *
 * function MyGrid() {
 *   const columnConfig = useProductGateColumns(onAddProducts, onChangeIgnoreFlag);
 *   return <AGGrid columnDefs={columnConfig.getAll()} />;
 * }
 * ```
 */

import { useFeatureFlags } from 'datastage/context/FeatureFlagContext';
import * as baseColumns from './columns';

/**
 * Hook that returns column definitions adapted for current feature flag state.
 *
 * @param {Function} onAddProducts - Callback for add/update actions
 * @param {Function} onChangeIgnoreFlag - Callback for ignore/unignore actions
 * @returns {Object} Column configuration with getAll() and getLimited() methods
 */
export const useProductGateColumns = (onAddProducts, onChangeIgnoreFlag) => {
    const { isHyperlaneEnabled } = useFeatureFlags();

    // Build columns with dynamic headers based on feature flag
    const columns = {
        // Action column (unchanged)
        action: baseColumns.action(onAddProducts),

        // Message column (unchanged)
        message: baseColumns.message,

        // ID column (unchanged)
        id: baseColumns.id,

        // Title → Title Name (Hyperlane)
        title: {
            ...baseColumns.title,
            headerName: isHyperlaneEnabled ? 'Title Name' : 'Title',
        },

        // Group ID → Enterprise Group ID (Hyperlane)
        groupId: {
            ...baseColumns.groupId,
            headerName: isHyperlaneEnabled ? 'Enterprise Group ID' : 'Group ID',
        },

        // Product ID → Enterprise Product ID (Hyperlane)
        productId: {
            ...baseColumns.productId,
            headerName: isHyperlaneEnabled ? 'Enterprise Product ID' : 'Product ID',
        },

        // Product Number → Enterprise Product Number (Hyperlane)
        productNumber: {
            ...baseColumns.productNumber,
            headerName: isHyperlaneEnabled ? 'Enterprise Product Number' : 'Product Number',
        },

        // Episode Title (unchanged)
        episodeTitle: baseColumns.episodeTitle,

        // Episode Number → Enterprise Episode Number (Hyperlane)
        episodeNumber: {
            ...baseColumns.episodeNumber,
            headerName: isHyperlaneEnabled ? 'Enterprise Episode Number' : 'Episode Num',
        },

        // Rightsline ID (unchanged)
        rightslineId: baseColumns.rightslineId,

        // Acted On Date (unchanged)
        actedOnDate: baseColumns.actedOnDate,

        // Acted On By (unchanged)
        actedOnBy: baseColumns.actedOnBy,

        // Updated Date (unchanged)
        updatedDate: baseColumns.updatedDate,

        // Ignore column (unchanged)
        ignore: baseColumns.ignore(onChangeIgnoreFlag),
    };

    // NEW COLUMNS: Only available in Hyperlane mode
    if (isHyperlaneEnabled) {
        // Parent Season Name (new in Hyperlane)
        columns.parentSeasonName = {
            field: 'parentSeasonName',
            headerName: 'Parent Season Name',
            headerComponent: baseColumns.message.headerComponent,
            headerComponentParams: {
                colId: 'parentSeasonName',
                page: 'productUpdates',
            },
            minWidth: 250,
            wrapText: true,
            autoHeight: true,
            cellStyle: {
                paddingTop: '10px',
                paddingBottom: '10px',
                wordBreak: 'normal',
                overflowWrap: 'break-word',
            },
        };

        // Releasing Company (new in Hyperlane, ESPN+ only)
        columns.releasingCompany = {
            field: 'releasingCompany',
            headerName: 'Releasing Company',
            headerComponent: baseColumns.message.headerComponent,
            headerComponentParams: {
                colId: 'releasingCompany',
                page: 'productUpdates',
            },
            minWidth: 200,
        };
    }

    /**
     * Returns all columns in display order for full access users.
     * Includes Hyperlane-specific columns when flag is enabled.
     *
     * @returns {Array} Array of column definitions for AG Grid
     */
    const getAll = () => {
        const allColumns = [
            columns.action,
            columns.message,
            columns.id,
            columns.title,
            columns.groupId,
            columns.productId,
            columns.productNumber,
            columns.episodeTitle,
            columns.episodeNumber,
            columns.rightslineId,
            columns.actedOnDate,
            columns.actedOnBy,
            columns.updatedDate,
            columns.ignore,
        ];

        // Add Hyperlane-specific columns
        if (isHyperlaneEnabled) {
            // Insert Parent Season Name after Episode Number
            const episodeNumberIndex = allColumns.indexOf(columns.episodeNumber);
            allColumns.splice(episodeNumberIndex + 1, 0, columns.parentSeasonName);

            // Add Releasing Company after Rightsline ID
            const rightslineIdIndex = allColumns.indexOf(columns.rightslineId);
            allColumns.splice(rightslineIdIndex + 1, 0, columns.releasingCompany);
        }

        return allColumns;
    };

    /**
     * Returns limited columns for read-only users.
     * Excludes action and ignore columns.
     *
     * @returns {Array} Array of column definitions for AG Grid
     */
    const getLimited = () => {
        const limitedColumns = [
            columns.message,
            columns.id,
            columns.title,
            columns.groupId,
            columns.productId,
            columns.productNumber,
            columns.episodeTitle,
            columns.episodeNumber,
            columns.rightslineId,
            columns.actedOnDate,
            columns.actedOnBy,
            columns.updatedDate,
        ];

        // Add Hyperlane-specific columns
        if (isHyperlaneEnabled) {
            // Insert Parent Season Name after Episode Number
            const episodeNumberIndex = limitedColumns.indexOf(columns.episodeNumber);
            limitedColumns.splice(episodeNumberIndex + 1, 0, columns.parentSeasonName);

            // Add Releasing Company after Rightsline ID
            const rightslineIdIndex = limitedColumns.indexOf(columns.rightslineId);
            limitedColumns.splice(rightslineIdIndex + 1, 0, columns.releasingCompany);
        }

        return limitedColumns;
    };

    return {
        columns,
        getAll,
        getLimited,
        isHyperlaneEnabled,
    };
};
